/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { useEmit } from '@accelint/bus/react';
import { clsx } from '@accelint/design-foundation/lib/utils';
import ChevronLeft from '@accelint/icons/chevron-left';
import { useContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Button } from 'react-aria-components/Button';
import { Header } from 'react-aria-components/Header';
import { Icon } from '../icon';
import { SidenavContext } from './context';
import { SidenavEventTypes } from './events';
import styles from './styles.module.css';
import type { SidenavHeaderProps, SidenavToggleEvent } from './types';

/**
 * SidenavHeader - Header component for sidenav
 *
 * Provides a header with toggle button for the sidenav
 *
 * @example
 * ```tsx
 * <Sidenav>
 *   <SidenavHeader>
 *     <SidenavAvatar>
 *       <Avatar>JD</Avatar>
 *       <Heading>John Doe</Heading>
 *     </SidenavAvatar>
 *   </SidenavHeader>
 * </Sidenav>
 * ```
 *
 * @param props - {@link SidenavHeaderProps}
 * @param props.children - Header content (typically SidenavAvatar).
 * @param props.classNames - Custom CSS class names for header elements.
 * @returns The rendered SidenavHeader component.
 */
export function SidenavHeader({
  children,
  classNames,
  ...rest
}: SidenavHeaderProps) {
  const emit = useEmit<SidenavToggleEvent>(SidenavEventTypes.toggle);
  const { id } = useContext(SidenavContext);

  return (
    <Header {...rest} className={clsx(styles.header, classNames?.header)}>
      <Button
        className={composeRenderProps(classNames?.button, (className) =>
          clsx(styles.toggle, className),
        )}
        onPress={() => emit({ id })}
      >
        {children}
        <Icon className={styles.transient}>
          <ChevronLeft />
        </Icon>
      </Button>
    </Header>
  );
}
